import React, { useState, useEffect, useContext, useRef } from "react";
import "./imageModal.css";
import { CombinedContext } from "../../../context/combinedContext";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import { Edit, Trash2 } from "lucide-react";

/** Simple mobile swipe carousel (controlled) */
function SwipeCarousel({ images = [], index, onChange }) {
	const { language } = useContext(CombinedContext);

    const trackRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const startX = useRef(0);
    const deltaX = useRef(0);

    const total = images.length || 1;

    const goTo = (n) => onChange(((n % total) + total) % total);
    const next = () => goTo(index + 1);
    const prev = () => goTo(index - 1);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [index, total]);

    const onStart = (clientX) => {
        setDragging(true);
        startX.current = clientX;
        deltaX.current = 0;
    };
    const onMove = (clientX) => {
        if (!dragging) return;
        deltaX.current = clientX - startX.current;
        if (trackRef.current) {
            trackRef.current.style.transition = "none";
            trackRef.current.style.transform = `translateX(calc(${-index * 100}% + ${deltaX.current}px))`;
        }
    };
    const onEnd = () => {
        setDragging(false);
        if (Math.abs(deltaX.current) > 60) {
            if (deltaX.current < 0) next();
            else prev();
        }
        if (trackRef.current) {
            trackRef.current.style.transition = "";
            trackRef.current.style.transform = `translateX(${-index * 100}%)`;
        }
        deltaX.current = 0;
    };

    useEffect(() => {
        if (trackRef.current) {
            trackRef.current.style.transition = "transform 300ms ease";
            trackRef.current.style.transform = `translateX(${-index * 100}%)`;
        }
    }, [index]);

    if (images.length === 0) {
        return (
            <div className="sf-image-preview-large">
                <div className="sf-image-placeholder">{translations[language].profile?.images?.noImage || "No image"}</div>
            </div>
        );
    }

    return (
        <div
            className="sf-mobile-carousel"
            onTouchStart={(e) => onStart(e.touches[0].clientX)}
            onTouchMove={(e) => onMove(e.touches[0].clientX)}
            onTouchEnd={onEnd}
            onMouseDown={(e) => {
                e.preventDefault();
                onStart(e.clientX);
                const mm = (evt) => onMove(evt.clientX);
                const mu = () => {
                    onEnd();
                    window.removeEventListener("mousemove", mm);
                    window.removeEventListener("mouseup", mu);
                };
                window.addEventListener("mousemove", mm);
                window.addEventListener("mouseup", mu, { once: true });
            }}
        >
            <div className="sf-mobile-carousel-track" ref={trackRef}>
                {images.map((img, i) => (
                    <div className="sf-mobile-carousel-slide" key={i} aria-hidden={i !== index}>
                        <img src={img} alt={`Slide ${i + 1}`} draggable={false} />
                    </div>
                ))}
            </div>

            {images.length > 1 && (
                <>
                    <button className="sf-shop-carousel-arrow sf-shop-carousel-arrow--left" onClick={prev}>
                        ‹
                    </button>
                    <button className="sf-shop-carousel-arrow sf-shop-carousel-arrow--right" onClick={next}>
                        ›
                    </button>
                </>
            )}
        </div>
    );
}

const ImageModal = ({ isOpen, onClose, shopId, initialImages = [], onSave }) => {
    const { language, setUserData, userData } = useContext(CombinedContext);

    // data
    const [images, setImages] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const [dragIndex, setDragIndex] = useState(null);
    const [saving, setSaving] = useState(false);


    // file inputs
    const addInputRef = useRef(null);
    const replaceInputRef = useRef(null);

    // responsive
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const mql = window.matchMedia("(max-width: 768px)");
        const update = () => setIsMobile(mql.matches);
        update();
        mql.addEventListener("change", update);
        return () => mql.removeEventListener("change", update);
    }, []);

    useEffect(() => {
        setImages(initialImages || []);
        setSelectedIndex(0);
    }, [initialImages]);

    if (!isOpen) return null;

    const currentImage = images[selectedIndex];

    const handleDragStart = (index) => setDragIndex(index);

    const handleDrop = (index) => {
        if (dragIndex === null || dragIndex === index) return;
        const updated = [...images];
        const [moved] = updated.splice(dragIndex, 1);
        updated.splice(index, 0, moved);
        setImages(updated);
        setDragIndex(null);
        setSelectedIndex(index);
    };

    const handleSave = async () => {
    try {
        setSaving(true);
        if (onSave) onSave(images);

        const res = await companyBasedRequest.updateShopImages({
            shopId,
            images,
            accessToken: userData.cognitoTokens.AccessToken,
        });

        setUserData((prev) => ({ ...prev, shop: res.shop }));
        onClose();
    } catch (err) {
        console.error("Failed to update shop images:", err);
    } finally {
        setSaving(false);
    }
};

    const handleOverlayClick = () => {
        if (!showConfirm) handleSave();
    };
    const handleDragOver = (e) => e.preventDefault();

    const handleDeleteRequest = (index) => {
        setDeleteIndex(index);
        setShowConfirm(true);
    };

    const confirmDelete = () => {
        removeAt(deleteIndex);
        setShowConfirm(false);
        setDeleteIndex(null);
    };

    const cancelDelete = () => {
        setShowConfirm(false);
        setDeleteIndex(null);
    };
    // ---- helpers

    const goRel = (d) => {
        if (images.length === 0) return;
        setSelectedIndex((i) => (i + d + images.length) % images.length);
    };

    const uploadAndGetUrl = async (file) => {
        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

        setUploading(true);
        try {
            const res = await companyBasedRequest.uploadShopImage({
                file: base64,
                fileName: file.name,
                fileType: file.type,
                shopId,
                accessToken: userData.cognitoTokens.AccessToken,
            });
            return res.fileUrl;
        } finally {
            setUploading(false);
        }
    };

    const handleAddFiles = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !shopId) return;
        try {
            const url = await uploadAndGetUrl(file);
            setImages((prev) => {
                const next = [...prev, url];
                if (prev.length === 0) setSelectedIndex(0);
                return next;
            });
        } catch (err) {
            console.error("Failed to upload image:", err);
        } finally {
            e.target.value = "";
        }
    };

    const handleReplaceFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !shopId) return;
        try {
            const url = await uploadAndGetUrl(file);
            setImages((prev) => {
                const next = [...prev];
                next[selectedIndex] = url;
                return next;
            });
        } catch (err) {
            console.error("Failed to upload replacement:", err);
        } finally {
            e.target.value = "";
        }
    };

    const removeAt = (index) => {
        setImages((prev) => {
            const next = prev.filter((_, i) => i !== index);
            if (next.length === 0) setSelectedIndex(0);
            else if (index >= next.length) setSelectedIndex(next.length - 1);
            return next;
        });
    };

    const setAsCover = () => {
        if (selectedIndex === 0) return;
        setImages((prev) => {
            const next = [...prev];
            const [img] = next.splice(selectedIndex, 1);
            next.unshift(img);
            return next;
        });
        setSelectedIndex(0);
    };

    const handleClose = () => {
        onSave?.(images);
        onClose();
    };

    // ---- RENDER
    return (
        <div className="sf-image-overlay" onClick={handleClose}>
            <div className="sf-image-modal" onClick={(e) => e.stopPropagation()}>
                {/* HEADER */}
                <div className="sf-image-modal-header">
                    <h2 className="sf-image-modal-title">
                        {translations[language].profile?.images?.title || "Images"}
                    </h2>
                    <button className="sf-image-close" onClick={handleClose}>
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className={`sf-image-modal-body ${isMobile ? "is-mobile" : "is-desktop"}`}>
                    {/* LEFT: thumbs (desktop) / bottom row (mobile has a second row below) */}
                    {!isMobile && (
                        <aside className="sf-image-aside">
                            <div className="sf-image-thumbs-vert">
                                {images.map((img, i) => (
                                    <button
                                        key={i}
                                        className={`sf-image-thumb ${i === selectedIndex ? "active" : ""}`}
                                        onClick={() => setSelectedIndex(i)}
                                        type="button"
                                        draggable
                                        onDragStart={() => handleDragStart(i)}
                                        onDrop={() => handleDrop(i)}
                                        onDragOver={handleDragOver}
                                    >
                                        <img src={img} alt={`Thumb ${i + 1}`} />
                                        {i === 0 && (
                                            <span className="sf-cover-badge">
                                                {translations[language].profile?.images?.cover || "Cover"}
                                            </span>
                                        )}
                                    </button>
                                ))}
                                <button
                                    className="sf-image-thumb add-thumb"
                                    onClick={() => addInputRef.current?.click()}
                                    type="button"
                                    title="Add image"
                                >
                                    +
                                </button>
                                <input
                                    ref={addInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={handleAddFiles}
                                />
                            </div>
                        </aside>
                    )}

                    {/* RIGHT: main preview */}
                    {/* RIGHT: main preview */}
                    <section className="sf-image-main">
                        {/* DESKTOP: single preview + arrows + actions */}
                        {!isMobile && (
                            <div className="sf-image-preview-large">
                                {currentImage ? (
                                    <img src={currentImage} alt="Selected" />
                                ) : (
                                    <div className="sf-image-placeholder">
                                        {translations[language].profile?.images?.noImageSelected || "No image"}
                                    </div>
                                )}

                                {/* actions */}
                                <div className="sf-image-actions">
                                    <button
                                        className="sf-image-act"
                                        onClick={setAsCover}
                                        disabled={images.length === 0 || selectedIndex === 0}
                                        type="button"
                                    >
                                        ★ {translations[language].profile?.images?.makeCover || "Set as cover"}
                                    </button>

                                    <button
                                        className="sf-image-act"
                                        onClick={() => replaceInputRef.current?.click()}
                                        disabled={images.length === 0}
                                        type="button"
                                    >
                                        <Edit size={14} />{" "}
                                        {translations[language].profile?.images?.replace || "Replace"}
                                    </button>
                                    <input
                                        ref={replaceInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={handleReplaceFile}
                                    />

                                    <button
                                        className="sf-image-act danger"
                                        onClick={() => handleDeleteRequest(selectedIndex)}
                                        disabled={images.length === 0}
                                        type="button"
                                    >
                                        <Trash2 size={20} strokeWidth={1.8} />{" "}
                                        {translations[language].profile?.images?.delete || "Delete"}
                                    </button>
                                </div>

                                {/* DESKTOP: prev/next arrows */}
                                {images.length > 1 && (
                                    <>
                                        <button
                                            className="sf-shop-carousel-arrow sf-shop-carousel-arrow--left"
                                            onClick={() => goRel(-1)}
                                            aria-label="Previous image"
                                            type="button"
                                        >
                                            ‹
                                        </button>
                                        <button
                                            className="sf-shop-carousel-arrow sf-shop-carousel-arrow--right"
                                            onClick={() => goRel(1)}
                                            aria-label="Next image"
                                            type="button"
                                        >
                                            ›
                                        </button>
                                    </>
                                )}
                            </div>
                        )}

                        {/* MOBILE: carousel ONLY (no duplicate static preview) with actions overlaid */}
                        {isMobile && (
                            <div className="sf-image-mobile-wrap">
                                <SwipeCarousel images={images} index={selectedIndex} onChange={setSelectedIndex} />

                                <div className="sf-image-actions">
                                    <button
                                        className="sf-image-act"
                                        onClick={setAsCover}
                                        disabled={images.length === 0 || selectedIndex === 0}
                                        type="button"
                                    >
                                        ★ {translations[language].profile?.images?.makeCover || "Set as cover"}
                                    </button>

                                    <button
                                        className="sf-image-act"
                                        onClick={() => replaceInputRef.current?.click()}
                                        disabled={images.length === 0}
                                        type="button"
                                    >
                                        <Edit size={14} />{" "}
                                        {translations[language].profile?.images?.replace || "Replace"}
                                    </button>
                                    <input
                                        ref={replaceInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={handleReplaceFile}
                                    />

                                    <button
                                        className="sf-image-act danger"
                                        onClick={() => handleDeleteRequest(selectedIndex)}
                                        disabled={images.length === 0}
                                        type="button"
                                    >
                                        <Trash2 size={20} strokeWidth={1.8} />{" "}
                                        {translations[language].profile?.images?.delete || "Delete"}
                                    </button>
                                </div>

                                {/* mobile thumbs row stays as you already render it below */}
                            </div>
                        )}

                        {/* MOBILE: horizontal thumbs + add (unchanged) */}
                        {isMobile && (
                            <>
                                <div className="sf-image-thumbs-row">
                                    <button
                                        className="sf-image-thumb add-thumb"
                                        onClick={() => addInputRef.current?.click()}
                                        type="button"
                                        title="Add image"
                                    >
                                        +
                                    </button>
                                    {images.map((img, i) => (
                                        <button
                                            key={i}
                                            className={`sf-image-thumb ${i === selectedIndex ? "active" : ""}`}
                                            onClick={() => setSelectedIndex(i)}
                                            type="button"
                                        >
                                            <img src={img} alt={`Thumb ${i + 1}`} />
                                            {i === 0 && (
                                                <span className="sf-cover-badge">
                                                    {translations[language].profile?.images?.cover || "Cover"}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                    <input
                                        ref={addInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={handleAddFiles}
                                    />
                                </div>
                            </>
                        )}
                    </section>
                </div>

                {/* FOOTER */}
                <div className="sf-base-footer">
                    <button className="btn ghost" onClick={onClose} disabled={uploading}>
                        {translations[language].calendar.editor.cancel}
                    </button>
                    <button className="btn" onClick={handleSave} disabled={saving}>
                        {saving
                            ? translations[language].calendar.editor.saving
                            : translations[language].calendar.editor.save}
                    </button>
                </div>
            </div>
            {showConfirm && (
                <div className="sf-confirm-overlay" onClick={cancelDelete}>
                    <div className="sf-confirm-box" onClick={(e) => e.stopPropagation()}>
                        <p>
                            {translations[language].profile?.images?.confirmDelete ||
                                "Are you sure you want to delete this image?"}
                        </p>
                        <div className="sf-confirm-actions">
                            <button onClick={cancelDelete}>
                                {translations[language].profile?.actions?.cancel || "Cancel"}
                            </button>
                            <button className="danger" onClick={confirmDelete}>
                                {translations[language].profile?.images?.delete || "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageModal;
